import { type ReactNode } from 'react';
import { AlertTriangle, HelpCircle } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';

// Cortex ConfirmationModal — gates destructive (or otherwise irreversible) actions behind
// an explicit confirm step (DESIGN_LANGUAGE_SPEC §27: "confirm modals on destructive
// actions"). Built on top of Modal; the `danger` variant uses the destructive button.

export type ConfirmationVariant = 'danger' | 'default';

export interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description?: ReactNode | undefined;
  variant?: ConfirmationVariant | undefined;
  confirmLabel?: string | undefined;
  cancelLabel?: string | undefined;
  /** Disables/loads the confirm button while the action is in flight. */
  loading?: boolean | undefined;
}

export function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  variant = 'default',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  loading = false,
}: ConfirmationModalProps) {
  const isDanger = variant === 'danger';

  const icon = isDanger ? (
    <AlertTriangle className="h-7 w-7" aria-hidden="true" />
  ) : (
    <HelpCircle className="h-7 w-7" aria-hidden="true" />
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      icon={icon}
      footer={
        <>
          <Button
            variant="secondary"
            fullWidth
            onClick={onClose}
            disabled={loading}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={isDanger ? 'destructive' : 'primary'}
            fullWidth
            loading={loading}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {description ? (
        <div className="text-sm theme-text-secondary">{description}</div>
      ) : null}
    </Modal>
  );
}
